import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
} from '@angular/core';
import { GoApplicationPreparationService } from '../../services/go-application-preparation/go-application-preparation.service';
import { Subject, takeUntil, tap } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { ApplicationPageFacadeService } from '../../../../direct-entry/shared/services/application-page-facade/application-page-facade.service';
import { RouteConstants } from '../../../../../../core/constants/routes';
import { GoApplicationRequestFormModel } from '../../models/go-application-request-form-model';
import { GoApplicationService } from '../../services/go-application/go-application.service';
import { ApplicationCreationResponse } from '../../../../shared/models/responses/application-creation-response';
import { Router } from '@angular/router';
import { GoStorageService } from '../../../go-parameters/services/go-storage/go-storage.service';

@Component({
    selector: 'app-go-application-preparation-base',
    changeDetection: ChangeDetectionStrategy.Eager,
    template:
        '<!-- This component serves as a base and does not render any content -->',
})
export abstract class GoApplicationPreparationBaseComponent<
        TFormModel extends Partial<GoApplicationRequestFormModel>,
    >
    implements OnInit, OnDestroy
{
    protected destroy$ = new Subject<void>();

    abstract form: FormGroup<FormGroupType<TFormModel>>;

    abstract hasChanges: boolean;

    constructor(
        protected readonly goApplicationPreparationService: GoApplicationPreparationService,
        protected readonly applicationPageFacadeService: ApplicationPageFacadeService,
        protected readonly goApplicationService: GoApplicationService,
        protected readonly router: Router,
        protected readonly goStorageService: GoStorageService,
    ) {}

    protected abstract beforeStepContinue(): Promise<boolean>;
    protected abstract shouldSubmit(): boolean;
    protected abstract patchFormWithGoApplicationRequestFormValue(): void;
    protected abstract scrollToFirstInvalidElement(): void;

    getDashboardRoute(): string {
        return RouteConstants.Dashboard.getFullRoute('INDEX');
    }

    handleNextStepClick(): void {
        this.applicationPageFacadeService.stepNavigationService.triggerNextClick();
    }

    get goApplicationRequestForm(): FormGroup<
        FormGroupType<GoApplicationRequestFormModel>
    > {
        return this.goApplicationPreparationService.getGoApplicationRequestForm();
    }

    ngOnInit(): void {
        this.patchFormWithGoApplicationRequestFormValue();

        this.applicationPageFacadeService.stepNavigationService.nextClick$
            .pipe(
                takeUntil(this.destroy$),
                tap(async () => {
                    const pageIsReadyForNext = await this.beforeStepContinue();
                    if (pageIsReadyForNext) {
                        this.handleGoApplicationStepContinuation();
                    }
                }),
            )
            .subscribe();

        this.applicationPageFacadeService.formEntryService.handleFormDirtying<TFormModel>(
            this.form,
            this.destroy$,
            (newValue: boolean) => (this.hasChanges = newValue),
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected handleGoApplicationStepContinuation(): void {
        if (this.form.valid) {
            this.goApplicationPreparationService.patchGoApplicationRequestForm(
                this.form.value,
            );

            this.hasChanges = false;

            if (this.shouldSubmit()) {
                const applicationId =
                    this.applicationPageFacadeService.applicationStateService.getCurrentApplicationState()
                        ?.applicationId;

                const requestForm =
                    this.goApplicationPreparationService.getValue(
                        this.goApplicationPreparationService.getGoApplicationRequestForm(),
                    );

                // If the application already exists, update and navigate to the search processing page
                if (applicationId) {
                    this.goApplicationService
                        .updateApplication(String(applicationId), requestForm)
                        .pipe(
                            tap(() =>
                                // TODO: handle failure state in future ticket...
                                this.router.navigate([
                                    RouteConstants.Go.getFullRouteWithApplicationId(
                                        'searchProcessing',
                                        String(applicationId),
                                    ),
                                ]),
                            ),
                        )
                        .subscribe();
                    return;
                }

                // Create the application and navigate to the search processing page
                this.goApplicationService
                    .createApplication(requestForm)
                    .pipe(
                        tap((response: ApplicationCreationResponse) => {
                            // TODO: handle failure state in future ticket...
                            this.router.navigate([
                                RouteConstants.Go.getFullRouteWithApplicationId(
                                    'searchProcessing',
                                    response.applicationId!.toString(),
                                ),
                            ]);
                        }),
                    )
                    .subscribe();
            } else {
                this.applicationPageFacadeService.stepNavigationService.goToNextStep();
            }
        } else {
            this.form.markAllAsTouched();
            this.scrollToFirstInvalidElement();
        }
    }

    /**
     * Will update the control values and validators on the given form to reflect the current state of the application.
     * @param form
     */
    protected refreshFormControlValuesAndValidators(
        form: FormGroup<FormGroupType<TFormModel>>,
    ): void {
        // Create a temporary form to hold the updated values since TFormModel is a subset of GoApplicationRequest
        // and may not have all the controls needed to fully represent the application state
        const tempForm =
            this.goApplicationPreparationService.getFormDefinition();

        tempForm.patchValue(this.goApplicationRequestForm.value, {
            emitEvent: false,
        });
        tempForm.patchValue(this.form.value, { emitEvent: false });

        this.goApplicationPreparationService.copyFormControlDisabledState(
            this.form,
            tempForm,
        );

        this.goApplicationPreparationService.updateGoApplicationFormControls(
            tempForm,
        );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.patchValue(tempForm.value as any, { emitEvent: false });
    }
}
